import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ElementRef,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DestroyBase } from '../../../../../shared/base-classes/destroy-component-base';
import { of, switchMap, takeUntil } from 'rxjs';
import MapView from '@arcgis/core/views/MapView';
import { ArcgisService } from '../../../services/arcgis/arcgis.service';
import { ArcgisAuthResponse } from '../../../models/arcgis-auth';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Home from '@arcgis/core/widgets/Home';
import Zoom from '@arcgis/core/widgets/Zoom';
import WebMap from '@arcgis/core/WebMap';
import { MapViewService } from '../../../services/arcgis/map-view.service';
import Search from '@arcgis/core/widgets/Search';
import { FilterConfig } from '../../../models/filter-config';
import { sidebarFiltersConfig } from '../../../configs/Site-Toolkit-filters-config';
import { QueryBuilderV2 } from '../../../models/query-builder-v2';
import { SiteToolkitFeatureInfoComponent } from '../site-toolkit-feature-info/site-toolkit-feature-info.component';
import { SiteToolkitFiltersComponent } from '../site-toolkit-filters/site-toolkit-filters.component';
import { SiteToolkitIntelligenceComponent } from '../site-toolkit-intelligence/site-toolkit-intelligence.component';
import { SiteToolkitBookmarksComponent } from '../site-toolkit-bookmarks/site-toolkit-bookmarks.component';
import { SiteIntelligenceService } from '../../../services/arcgis/site-intelligence.service';
import esriId from '@arcgis/core/identity/IdentityManager';
import esriConfig from '@arcgis/core/config.js';
import { AuditLogService } from '../../../../../shared/services/audit-log/audit-log.service';
import { LoaderService } from '../../../../../shared/services/loader/loader.service';
import { SearchService } from '../../../../search/services/search.service';
import { Properties } from '../../../../../shared/models/company-details';
import { RecordDetailsComponent } from '../../../../../shared/components/record-details/record-details.component';

@Component({
  selector: 'app-site-toolkit',
  standalone: true,
  imports: [
    SiteToolkitFeatureInfoComponent,
    SiteToolkitFiltersComponent,
    SiteToolkitIntelligenceComponent,
    SiteToolkitBookmarksComponent,
    RecordDetailsComponent,
  ],
  templateUrl: './site-toolkit.component.html',
  styleUrl: './site-toolkit.component.scss',
})
export class SiteToolkitComponent
  extends DestroyBase
  implements OnInit, OnChanges, OnDestroy
{
  readonly ALLOWED_TABS = [
    'mapsFiltersSearch',
    'siteIntelligence',
    'siteBookmarks',
  ] as const;

  @Input({ required: true }) arcgisToken!: string;
  @Input({ required: true }) toolkitTitle!: string;

  @ViewChild('mapViewNode', { static: true }) private mapViewEl!: ElementRef;

  arcgisService = inject(ArcgisService);
  mapViewService = inject(MapViewService);
  siteIntelligenceService = inject(SiteIntelligenceService);
  auditLogService = inject(AuditLogService);
  private route = inject(ActivatedRoute);
  private loaderService = inject(LoaderService);
  private searchService = inject(SearchService);

  mapView!: MapView;
  featureLayer!: FeatureLayer;
  isLoading = true;
  isMapLoaded = false;
  sidebarFilters: FilterConfig[] = sidebarFiltersConfig;
  featureLayerQuery: QueryBuilderV2;
  activeTab = 'mapsFiltersSearch'; // Default active tab
  isFullscreen = false;

  @ViewChild('dashboardContainerDiv', { static: false })
  dashboardContainerDiv!: ElementRef;

  shouldScrollToMap = false;
  leadId: string | null = null;
  leadInfo: Record<string, string>[] = [];
  showRecordDetailsPopup = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handlePopupClickBound: any;
  constructor() {
    super();
    this.featureLayerQuery = new QueryBuilderV2();
  }

  async ngOnInit() {
    // Check for activeTab query param
    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        switchMap((params) => {
          if (
            params['activeTab'] &&
            this.ALLOWED_TABS.includes(params['activeTab'])
          ) {
            this.activeTab = params['activeTab'];
          }

          if (
            params['map'] === 'site-toolkit' &&
            params['activeTab'] === 'siteIntelligence'
          ) {
            this.shouldScrollToMap = true;
          }

          if (params['leadId']) {
            this.leadId = params['leadId'];
            return this.searchService.getCompanyDetails({
              unique_id: params['leadId'],
            });
          }

          return of(null);
        }),
      )
      .subscribe((companyDetails) => {
        const properties = new Set([
          'lead_info::lead_status',
          'company_name',
          'address',
          'address_line_2',
          'city',
          'state',
          'postal_code',
          'county',
          'zip_code',
          'email',
        ]);
        companyDetails?.detail?.properties?.forEach((property: Properties) => {
          if (properties.has(property.property)) {
            const propertyInfo = {
              property_name: property.property_name,
              value: property.value ? property.value : '',
            };
            this.leadInfo.push(propertyInfo);
          }
        });
      });

    if (!this.arcgisToken) {
      console.error('ArcGIS token is required');
      return;
    }

    try {
      let isTokenValid = await this.arcgisService.setArcgisAuthContext(
        this.arcgisToken,
      );
      if (!isTokenValid) {
        console.log('Initial token is invalid, attempting token renewal...');

        // Convert the subscription to a promise for better async handling
        const authResponse = await new Promise<ArcgisAuthResponse>(
          (resolve) => {
            this.arcgisService
              .getM2MToken()
              .pipe(takeUntil(this.destroy$))
              .subscribe((response: ArcgisAuthResponse) => {
                resolve(response);
              });
          },
        );

        this.arcgisToken = authResponse.token.access_token;
        isTokenValid = await this.arcgisService.setArcgisAuthContext(
          this.arcgisToken,
        );

        if (!isTokenValid) {
          console.error('⚠️ Failed to validate token even after renewal');
          return;
        }
      }

      this.initializeArcGIS();
    } catch (error) {
      console.error('Error during token validation:', error);
      return;
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['toolkitTitle'] && !changes['toolkitTitle'].firstChange) {
      this.isLoading = true;
      this.isMapLoaded = false;
      this.activeTab = 'mapsFiltersSearch';
      this.siteIntelligenceService.clearData(); // Clear stored data when toolkit changes
      await this.initializeArcGIS(); // Reinitialize the map with the new title
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.mapView?.container) {
      this.mapView.container.removeEventListener(
        'click',
        this.handlePopupClickBound,
      );
    }
    if (this.mapView?.popup && typeof this.mapView.popup.close === 'function') {
      this.mapView.popup.close();
    }
    if (this.mapView) {
      this.mapView.destroy();
    }
  }

  async initializeArcGIS() {
    try {
      if (this.shouldScrollToMap) {
        this.loaderService.show();
      }

      esriConfig.portalUrl = 'https://arcgis.accdata.org/server';
      esriId.registerToken({
        server: 'https://arcgis.accdata.org/server',
        token: this.arcgisToken,
      });

      const lat = parseFloat(this.route.snapshot.queryParams['lat']);
      const lon = parseFloat(this.route.snapshot.queryParams['lon']);
      const isValidCoords = !isNaN(lat) && !isNaN(lon);

      const map = new WebMap({
        portalItem: {
          id: 'ea5973629e45494f9e97bb2401633f06',
          access: 'org',
        },
      });

      const mapViewConfig = {
        map: map,
        container: 'mapViewDiv',
        center: isValidCoords ? [lon, lat] : [-82.5618, 36.5484],
        zoom: 7,

        ...(this.shouldScrollToMap && {
          popup: {
            autoCloseEnabled: false,
            visibleElements: {
              closeButton: false,
              actionBar: false,
            },
            dockEnabled: false,
            dockOptions: {
              buttonEnabled: false,
              breakpoint: false,
            },
          },
        }),
      };

      this.mapView = new MapView(mapViewConfig);

      if (this.shouldScrollToMap) {
        // This stops the MapView from dismissing the popup on background clicks
        this.mapView.on('click', (event) => {
          event.stopPropagation();
        });
      }

      this.mapViewService.setMapView(this.mapView);

      await this.mapView.when();

      this.isMapLoaded = true;

      if (isValidCoords && this.shouldScrollToMap) {
        this.showInitialPopup(lat, lon);
      }

      this.addWidgets();

      this.isLoading = false;
      this.loaderService.hide();
    } catch (error) {
      console.error('Error initializing ArcGIS:', error);
      this.isLoading = false;
      this.loaderService.hide();
    }
  }

  showInitialPopup(lat: number, lon: number) {
    if (!this.mapView) return;

    const point = {
      type: 'point',
      longitude: lon,
      latitude: lat,
    };

    const popupContent = this.leadInfo
      .map(
        (item) =>
          `<div class="info-row"><span class="info-label">${item['property_name']}:</span> <span class="info-value">${item['value'] || '-'}</span></div>`,
      )
      .join('');

    const content = `
    <div class="custom-popup-content">
      ${popupContent || '<div class="no-info">No lead information available</div>'}
      <div class="view-details">View More Details →</div>
    </div>
  `;

    this.mapView.openPopup({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      location: point as any,
      title: 'Lead Information',
      content: content || 'No lead information available',
    });

    const mapContainer = this.mapView.container;
    mapContainer.removeEventListener('click', this.handlePopupClickBound);

    this.handlePopupClickBound = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target && target.classList.contains('view-details')) {
        this.showRecordDetailsPopup = true;
      }
    };

    mapContainer.addEventListener('click', this.handlePopupClickBound);
  }

  addWidgets() {
    // remove existing widgets
    this.mapView.ui.empty('top-left');
    this.mapView.ui.empty('top-right');
    this.mapView.ui.empty('bottom-left');
    this.mapView.ui.empty('bottom-right');

    const searchWidget = new Search({
      view: this.mapView,
    });

    searchWidget.on('search-start', () => {
      this.auditLogService.logFeature({
        featureName: 'search in arcgis',
        action: 'search',
        meta: { searchInitiated: true },
        description: 'User initiates search in ArcGIS map',
      });
    });
    this.mapView.ui.add(searchWidget, {
      position: 'top-leading',
      index: 1,
    });

    const zoom = new Zoom({
      view: this.mapView,
      layout: 'vertical',
    });
    // Add the Zoom widget to the top left corner of the view
    this.mapView.ui.add(zoom, { position: 'top-left', index: 1 });

    const homeWidget = new Home({
      view: this.mapView,
    });
    // adds the home widget to the top left corner of the MapView
    this.mapView.ui.add(homeWidget, { position: 'top-left', index: 2 });
  }

  setActiveTab(tab: string) {
    if (this.ALLOWED_TABS.includes(tab as (typeof this.ALLOWED_TABS)[number])) {
      this.activeTab = tab;
    } else {
      console.warn(`Invalid tab: ${tab}. Using default tab.`);
      this.activeTab = 'mapsFiltersSearch';
    }
  }

  onFilterChange(filter: FilterConfig) {
    const layer = this.mapView.map.layers.find(
      (layer) => layer.title === 'USA Structures',
    );

    if (layer && layer instanceof FeatureLayer) {
      if (filter.selectedValue === '') {
        layer.definitionExpression = ''; // Remove the where condition
      } else {
        layer.definitionExpression =
          filter.field + " = '" + filter.selectedValue + "'";
      }
    } else {
      console.error(
        'Layer with title "USA Structures" not found or is not a FeatureLayer.',
      );
    }
  }

  toggleFullScreen() {
    const elem = this.dashboardContainerDiv.nativeElement;

    if (!document.fullscreenElement) {
      elem.requestFullscreen().catch((err: Error) => {
        console.error(
          `Error attempting to enable full-screen mode: ${err.message}`,
        );
      });
      this.isFullscreen = true;
    } else {
      document.exitFullscreen();
      this.isFullscreen = false;
    }
  }
}
